import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, Send, Star, Lightbulb, Bug, Palette, 
  BookOpen, CheckCircle, Clock, XCircle, DollarSign, Search
} from 'lucide-react';
import { getComics, submitFeedback, getMyFeedback } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const FEEDBACK_CATEGORIES = [
  { id: 'story', name: 'Story Suggestion', icon: BookOpen, color: 'text-blue-500' },
  { id: 'character', name: 'Character Idea', icon: Star, color: 'text-yellow-500' },
  { id: 'art', name: 'Art Feedback', icon: Palette, color: 'text-purple-500' },
  { id: 'bug', name: 'Bug Report', icon: Bug, color: 'text-red-500' },
  { id: 'feature', name: 'Feature Idea', icon: Lightbulb, color: 'text-green-500' },
];

export function FeedbackSubmitForm({ onSuccess }) {
  const [comics, setComics] = useState([]);
  const [selectedComic, setSelectedComic] = useState(null);
  const [category, setCategory] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1); // 1: select comic, 2: write feedback

  useEffect(() => {
    fetchComics();
  }, []);

  const fetchComics = async () => {
    try {
      const res = await getComics({ limit: 100 });
      setComics(res.data);
    } catch (error) {
      console.error('Error fetching comics:', error);
    }
  };

  const filteredComics = comics.filter(comic => 
    comic.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    comic.creator_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedComic || !category || !title || !content) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmitting(true);
    try {
      await submitFeedback({
        comic_id: selectedComic.id,
        title,
        content,
        category
      });
      
      toast.success('Feedback submitted! You\'ll be notified if the creator uses your idea.');
      setSelectedComic(null);
      setCategory('');
      setTitle('');
      setContent('');
      setStep(1);
      onSuccess?.();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="feedback-submit-form">
      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4 mb-8">
        <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[var(--primary)]' : 'text-[var(--text-secondary)]'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 1 ? 'bg-[var(--primary)] text-white' : 'bg-[var(--surface)] border border-[var(--border)]'}`}>
            1
          </div>
          <span className="hidden sm:inline">Select Comic</span>
        </div>
        <div className="w-12 h-0.5 bg-[var(--border)]" />
        <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[var(--primary)]' : 'text-[var(--text-secondary)]'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 2 ? 'bg-[var(--primary)] text-white' : 'bg-[var(--surface)] border border-[var(--border)]'}`}>
            2
          </div>
          <span className="hidden sm:inline">Write Feedback</span>
        </div>
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-xl font-teko">Select a Comic to Give Feedback</h3>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
            <input
              type="text"
              placeholder="Search comics or creators..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-[var(--surface)] border border-[var(--border)] rounded-lg focus:outline-none focus:border-[var(--primary)]"
              data-testid="comic-search"
            />
          </div>

          {/* Comic Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-h-96 overflow-y-auto p-1">
            {filteredComics.map(comic => (
              <button
                key={comic.id}
                onClick={() => {
                  setSelectedComic(comic);
                  setStep(2);
                }}
                className={`text-left p-3 rounded-xl border-2 transition-all hover:border-[var(--primary)]/50 ${
                  selectedComic?.id === comic.id ? 'border-[var(--primary)] bg-[var(--primary)]/5' : 'border-[var(--border)]'
                }`}
                data-testid={`comic-option-${comic.id}`}
              >
                <img 
                  src={comic.cover_image} 
                  alt={comic.title}
                  className="w-full aspect-[3/4] object-cover rounded-lg mb-2"
                />
                <p className="font-medium text-sm line-clamp-1">{comic.title}</p>
                <p className="text-xs text-[var(--text-secondary)]">{comic.creator_name}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 2 && selectedComic && (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Selected Comic Preview */}
          <div className="flex items-center gap-4 p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl">
            <img 
              src={selectedComic.cover_image} 
              alt={selectedComic.title}
              className="w-16 h-20 object-cover rounded-lg"
            />
            <div className="flex-1">
              <p className="font-teko text-xl">{selectedComic.title}</p>
              <p className="text-sm text-[var(--text-secondary)]">by {selectedComic.creator_name}</p>
            </div>
            <button
              type="button"
              onClick={() => setStep(1)}
              className="text-sm text-[var(--primary)] hover:underline"
            >
              Change
            </button>
          </div>

          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium mb-3">Feedback Category</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {FEEDBACK_CATEGORIES.map(cat => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => setCategory(cat.id)}
                    className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                      category === cat.id 
                        ? 'border-[var(--primary)] bg-[var(--primary)]/5' 
                        : 'border-[var(--border)] hover:border-[var(--primary)]/50'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <Icon size={18} className={cat.color} />
                    <span className="text-sm">{cat.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of your feedback"
              className="w-full px-4 py-3 bg-[var(--surface)] border border-[var(--border)] rounded-lg focus:outline-none focus:border-[var(--primary)]"
              required
              data-testid="feedback-title"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium mb-2">Your Feedback</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Describe your idea, suggestion, or feedback in detail..."
              rows={6}
              className="w-full px-4 py-3 bg-[var(--surface)] border border-[var(--border)] rounded-lg focus:outline-none focus:border-[var(--primary)] resize-none"
              required
              data-testid="feedback-content"
            />
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              {content.length}/1000 characters
            </p>
          </div>

          {/* Submit */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(1)}
              className="flex-1"
            >
              Back
            </Button>
            <Button
              type="submit"
              disabled={submitting || !category || !title || !content}
              className="flex-1 bg-[var(--primary)] hover:bg-[var(--primary)]/90"
              data-testid="submit-feedback-btn"
            >
              {submitting ? (
                'Submitting...'
              ) : (
                <>
                  <Send size={18} className="mr-2" />
                  Submit Feedback
                </>
              )}
            </Button>
          </div>

          {/* Info */}
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-sm">
            <p className="font-medium text-green-500 mb-1">💡 Earn Money from Your Ideas!</p>
            <p className="text-[var(--text-secondary)]">
              If the creator uses your feedback, you'll receive a payment. 
              Quality, detailed feedback has a higher chance of being selected.
            </p>
          </div>
        </form>
      )}
    </div>
  );
}

export function FeedbackList() {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      const res = await getMyFeedback();
      setFeedback(res.data);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="text-green-500" size={18} />;
      case 'rejected':
        return <XCircle className="text-red-500" size={18} />;
      default:
        return <Clock className="text-yellow-500" size={18} />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Not Selected';
      default:
        return 'Under Review';
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={`feedback-skeleton-${i}`} className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl animate-pulse">
            <div className="h-5 w-1/3 bg-[var(--border)] rounded mb-2" />
            <div className="h-4 w-2/3 bg-[var(--border)] rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (feedback.length === 0) {
    return (
      <div className="text-center py-12 bg-[var(--surface)] border border-[var(--border)] rounded-xl">
        <MessageSquare size={48} className="mx-auto text-[var(--text-secondary)] mb-4" />
        <p className="text-lg font-medium mb-2">No feedback submitted yet</p>
        <p className="text-[var(--text-secondary)]">
          Submit feedback on your favorite comics to earn money!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="feedback-list">
      {feedback.map(fb => (
        <div 
          key={fb.id}
          className="p-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                {getStatusIcon(fb.status)}
                <span className="text-sm text-[var(--text-secondary)]">
                  {getStatusLabel(fb.status)}
                </span>
              </div>
              <h3 className="font-teko text-xl">{fb.title}</h3>
              <p className="text-sm text-[var(--text-secondary)] mb-2">
                {FEEDBACK_CATEGORIES.find(c => c.id === fb.category)?.name || fb.category}
              </p>
              <p className="text-[var(--text-secondary)] line-clamp-2">{fb.content}</p>
              <p className="text-xs text-[var(--text-secondary)] mt-2">
                {new Date(fb.created_at).toLocaleDateString()}
              </p>
            </div>
            
            {fb.payment_amount > 0 && (
              <div className="text-right">
                <div className="flex items-center gap-1 text-green-500 font-bold">
                  <DollarSign size={16} />
                  {fb.payment_amount.toFixed(2)}
                </div>
                <p className="text-xs text-[var(--text-secondary)]">Earned</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default FeedbackSubmitForm;
